import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit} from '@angular/core';
import {DetectMethodChanges} from "../../utils";
import {
    OffsetsManager,
    VisibleOffsetsSettingsService,
    VisibleOffsetsShowMode
} from "../visible-offsets/visible-offsets-settings.service";
import {FavoriteAdjustment} from "./FavoriteAdjustment";
import {SessionService} from "../../authentication/session-service.service";

@Component({
    selector: 'ets-pricebox-menu-component',
    templateUrl: 'pricebox-menu.component.html',
    styleUrls: ['pricebox-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class PriceboxMenuComponent implements OnInit {
    constructor(
        private _changeDetector: ChangeDetectorRef,
        private _visibleOffsetsSettingsService: VisibleOffsetsSettingsService,
        private _sessionService: SessionService
    ) {
    }

    // O73: the events-list tooltip row is shown to super users only. A ROLE rule, read from the
    // session, not a grantable secure element.
    get isSuperUser(): boolean {
        return this._sessionService.isSuperUser;
    }

    visible = false;

    showSpreadOffset: boolean;
    showSpreadWidth: boolean;
    showSecondSpreadOffset: boolean;
    showSecondSpreadWidth: boolean;
    showProtectiveOptionOffset: boolean;
    showSecondProtectiveOptionOffset: boolean;

    showOffsets: boolean;

    availableShowModes: VisibleOffsetsShowMode[] = [
        'Displayed',
        'Mouse Over'
    ];

    showMode: VisibleOffsetsShowMode;

    showSingleValue = false;

    spreadOffsetFilter: number;
    spreadWidthFilter: number;

    showImpliedVolatility: boolean;
    showZonesGridArrow: boolean;
    showPricingStepsIcon: boolean;

    // CR-20260904-001 (Presentation, BR-P2): the three Price Box landmine highlight switches, moved
    // here from Application Settings on the owner's ruling. View only — toggling one changes nothing
    // about an ORION run (BR-P3).
    highlightMondays: boolean;
    highlightTier1: boolean;
    highlightTier2: boolean;
    // O73: fourth switch, super users only — the events-list tooltip on a coloured header.
    showLandmineEventsTooltip: boolean;

    isPricingStepsAvailable = false;

    settingsSaved$ = new EventEmitter();

    availableCallAdjustments : string[] = [
        '#1(C)',
        '#2A(C)',
        '#2B(C)',
        '#3(C)',
        '#3DO(C)'
    ];

    availablePutAdjustments : string[] = [
        '#1(P)',
        '#2A(P)',
        '#2B(P)',
        '#3(P)',
        '#3DO(P)',
    ];

    favoriteAdjustments : FavoriteAdjustment[] = [];

    private get offsetsManager(): OffsetsManager {
        return this._visibleOffsetsSettingsService.priceBox;
    }

    ngOnInit() {
    }

    @DetectMethodChanges()
    show() {
        this.setWhatToShow();
        this.setWhenAndHowToShow();
        this.setFilters();
        this.setFavorites();
        this.setHighlights();
        this.visible = true;
    }

    @DetectMethodChanges()
    onHidden() {
        this.visible = false;
    }

    private setWhatToShow() {
        this.showSpreadOffset = this.offsetsManager.showSpreadOffset;
        this.showSpreadWidth = this.offsetsManager.showSpreadWidth;
        this.showSecondSpreadOffset = this.offsetsManager.showSecondSpreadOffset;
        this.showSecondSpreadWidth = this.offsetsManager.showSecondSpreadWidth;
        this.showProtectiveOptionOffset = this.offsetsManager.showProtectiveOptionOffset;
        this.showSecondProtectiveOptionOffset = this.offsetsManager.showSecondProtectiveOptionOffset;
        this.showSingleValue = this.offsetsManager.showSingleValue;
        this.showImpliedVolatility = this.offsetsManager.showImpliedVolatility;
        this.showZonesGridArrow= this.offsetsManager.showZonesGridArrow;
        this.showPricingStepsIcon = this.offsetsManager.showPricingStepsIcon;
    }

    private setWhenAndHowToShow() {
        this.showOffsets = this.offsetsManager.showOffsets;
        this.showMode = this.offsetsManager.showMode;
    }

    private setFilters() {
        this.spreadOffsetFilter = this.offsetsManager.spreadOffsetFilter;
        this.spreadWidthFilter = this.offsetsManager.spreadWidthFilter;
    }

    private setFavorites() {
        const json = this.offsetsManager.favoriteAdjustments;
        let obj : FavoriteAdjustment[] = JSON.parse(json) || [];
        this.favoriteAdjustments = obj || [];
    }

    // CR-20260904-001 (Presentation): read on open, written on Save — the same read/save pair every
    // other group in this dialog uses, so Close discards exactly as it does for the rest.
    private setHighlights() {
        this.highlightMondays = this.offsetsManager.highlightMondays;
        this.highlightTier1 = this.offsetsManager.highlightTier1;
        this.highlightTier2 = this.offsetsManager.highlightTier2;
        this.showLandmineEventsTooltip = this.offsetsManager.showLandmineEventsTooltip;
    }

    saveSettings() {
        this.saveWhatToShow();
        this.saveWhenAndHowToShow();
        this.saveFilters();
        this.saveFavorites();
        this.saveHighlights();
        this.close();
        this.settingsSaved$.emit();
    }

    private saveHighlights() {
        this.offsetsManager.highlightMondays = this.highlightMondays;
        this.offsetsManager.highlightTier1 = this.highlightTier1;
        this.offsetsManager.highlightTier2 = this.highlightTier2;
        // Only a super user can see (and so change) this row; a regular user's Save writes back
        // the value that was read, so nothing is silently reset.
        this.offsetsManager.showLandmineEventsTooltip = this.showLandmineEventsTooltip;
    }

    private saveWhatToShow() {
        this.offsetsManager.showSpreadOffset = this.showSpreadOffset;
        this.offsetsManager.showSpreadWidth = this.showSpreadWidth;
        this.offsetsManager.showSecondSpreadOffset = this.showSecondSpreadOffset;
        this.offsetsManager.showSecondSpreadWidth = this.showSecondSpreadWidth;
        this.offsetsManager.showProtectiveOptionOffset = this.showProtectiveOptionOffset;
        this.offsetsManager.showSecondProtectiveOptionOffset = this.showSecondProtectiveOptionOffset;
        this.offsetsManager.showSingleValue = this.showSingleValue;
        this.offsetsManager.showImpliedVolatility = this.showImpliedVolatility;
        this.offsetsManager.showZonesGridArrow = this.showZonesGridArrow;
        this.offsetsManager.showPricingStepsIcon = this.showPricingStepsIcon;
    }

    private saveWhenAndHowToShow() {
        this.offsetsManager.showMode = this.showMode;
        this.offsetsManager.showOffsets = this.showOffsets;
    }

    private saveFilters() {
        this.offsetsManager.spreadOffsetFilter = this.spreadOffsetFilter;
        this.offsetsManager.spreadWidthFilter = this.spreadWidthFilter;
    }

    private saveFavorites() {
        const favs = JSON.stringify(this.favoriteAdjustments || []);
        this.offsetsManager.favoriteAdjustments = favs;
    }

    @DetectMethodChanges()
    close() {
        this.visible = false;
    }

    @DetectMethodChanges()
    addFavoriteAdjustment() {
        this.favoriteAdjustments.push({});
    }

    @DetectMethodChanges()
    removeFavoriteAdjustment(fav: FavoriteAdjustment) {
        const number = this.favoriteAdjustments.indexOf(fav);
        if (number<0) {
            return;
        }
        this.favoriteAdjustments.splice(number, 1);
    }

    onValueChanged($event: any) {
        console.log($event);
    }
}
